import React from 'react';
import {
    Page,
    Text,
    View,
    Document,
    StyleSheet,
    Font,
    Image,
} from '@react-pdf/renderer';
import { RateLookupFormData, RateData } from './types';
import { readSavedEndorsementRate } from './endorsementRates';
import { UserIdentity } from 'react-admin';

// Register font (optional)
// Font.register({ family: 'Helvetica', src: '/path/to/Helvetica.ttf' });

// Define styles (merged from HPTProposalPDF and adjusted)
const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
        paddingTop: 30,
        paddingHorizontal: 30,
        paddingBottom: 65, // room for fixed footer
        // fontFamily: 'Roboto' // Use registered font
        fontSize: 10,
    },
    header: {
        fontSize: 16,
        marginBottom: 20,
        textAlign: 'center',
        textDecoration: 'underline',
    },
    section: {
        marginBottom: 15,
        padding: 10,
        border: '1pt solid #cccccc',
        borderRadius: 3,
    },
    sectionTitle: {
        fontSize: 12,
        marginBottom: 8,
        fontWeight: 'bold',
        textDecoration: 'underline',
    },
    formRow: {
        flexDirection: 'row',
        marginBottom: 4,
    },
    formLabel: {
        width: '30%',
        fontWeight: 'bold',
    },
    formValue: {
        width: '70%',
    },
    table: {
        width: 'auto',
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#bfbfbf',
        borderRightWidth: 0,
        borderBottomWidth: 0,
        marginTop: 10,
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomColor: '#bfbfbf',
        borderBottomWidth: 1,
        alignItems: 'center',
        minHeight: 18, // Adjust as needed
    },
    tableColHeader: {
        // width: '11%', // Adjust based on number of columns
        borderStyle: 'solid',
        borderColor: '#bfbfbf',
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
        backgroundColor: '#f2f2f2', // Light grey background for header
        padding: 4,
        fontWeight: 'bold',
        textAlign: 'center',
    },
    tableCol: {
        // width: '11%', // Adjust based on number of columns
        borderStyle: 'solid',
        borderColor: '#bfbfbf',
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
        padding: 4,
        textAlign: 'center',
        borderBottom: '1pt solid #e0e0e0',
    },
    aipCell: {
        // Specific style for the AIP column
        width: '20%', // Give AIP column more fixed width
        textAlign: 'left', // Align AIP names left
        paddingLeft: 8, // Add specific left padding
    },
    productCell: {
        // Style for the dynamic product columns
        textAlign: 'center',
        padding: 6, // Increased padding
    },
    productHeaderCell: {
        // Style for the dynamic product header columns
        textAlign: 'center',
        fontWeight: 'bold',
        padding: 6, // Increased padding
        backgroundColor: '#f2f2f2',
    },
    productHeaderColumnBorders: {
        borderLeftWidth: 2,
        borderRightWidth: 2,
        borderLeftColor: '#555',
        borderRightColor: '#555',
        borderStyle: 'solid',
    },
    rateCostDividerBorder: {
        borderRightWidth: 1,
        borderRightColor: '#bbb',
        borderStyle: 'solid',
    },
    costAcreRightBorder: {
        borderRightWidth: 2,
        borderRightColor: '#555',
        borderStyle: 'solid',
    },
    // Specific column widths (adjust percentages to sum roughly to 100)
    colCarrier: { width: '20%' },
    colYear: { width: '10%' },
    colState: { width: '8%' },
    colCounty: { width: '15%' },
    colTownship: { width: '10%' },
    colRange: { width: '10%' },
    colForm: { width: '20%' },
    colRate: { width: '7%', textAlign: 'right' }, // Rate column narrower and right-aligned

    watermark: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        opacity: 0.1,
    },

    topRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 20,
    },
    topLogoBox: {
        width: 100,
        height: 40,
        marginBottom: 6,
        marginLeft: 'auto',
        justifyContent: 'center',
        alignItems: 'center',
    },

    logo: {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
    },

    topLeft: {
        flexDirection: 'column',
        flexGrow: 1,
    },
    farmName: {
        fontSize: 16,
        fontWeight: 'bold',
        marginBottom: 5,
    },
    hptToolLabel: {
        fontSize: 12,
        fontWeight: 'bold',
    },
    tagContainer: {
        flexDirection: 'row',
        marginTop: 5,
    },
    tag: {
        backgroundColor: '#004DD5', // Changed from #f2f2f2 to CropWest green
        paddingVertical: 2,
        paddingHorizontal: 10,
        borderRadius: 12,
        marginRight: 6,
        marginBottom: 4,
    },
    tagText: {
        fontSize: 10,
        color: '#FFFFFF',
        fontWeight: 'bold',
    },
    tagIpa: {
        backgroundColor: '#2e7d32',
        paddingVertical: 2,
        paddingHorizontal: 10,
        borderRadius: 12,
        marginRight: 6,
        marginBottom: 4,
    },
    rightRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    contactRight: {
        flexDirection: 'column',
        alignItems: 'flex-end',
    },
    agencyText: {
        fontSize: 12,
        fontWeight: 'bold',
        textAlign: 'right',
    },
    agentRow: {
        flexDirection: 'row',
        marginTop: 2,
    },
    agentText: {
        fontSize: 10,
    },
    agentPhone: {
        fontSize: 10,
        marginLeft: 10,
    },
    agentEmail: {
        fontSize: 10,
    },
    logoBox: {
        backgroundColor: '#f2f2f2',
        padding: 5,
        borderRadius: 5,
    },
    logoText: {
        fontSize: 12,
        fontWeight: 'bold',
    },
    topSpacer: {
        height: 20,
    },
    chipsAboveTable: {
        marginBottom: 10,
    },
    tableSectionLabel: {
        fontSize: 12,
        fontWeight: 'bold',
        marginTop: 16,
        marginBottom: 6,
    },
    tableTitleRow: {
        flexDirection: 'row',
        borderBottomColor: '#bfbfbf',
        borderBottomWidth: 1,
        alignItems: 'center',
        backgroundColor: '#e8e8e8',
    },
    tableTitleCell: {
        width: '100%',
        padding: 8,
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: 11,
    },
    tableHeader: {
        backgroundColor: '#f2f2f2',
    },
    tableCell: {
        padding: 5,
        borderBottom: '1pt solid #e0e0e0',
        textAlign: 'center',
    },
    note: {
        marginTop: 20,
        textAlign: 'center',
    },
    bottomCenterSection: {
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 30,
    },

    bottomLogoBox: {
        width: 120,
        height: 40,
        marginBottom: 6,
        justifyContent: 'center',
        alignItems: 'center',
    },

    cropWestText: {
        fontSize: 10,
        fontStyle: 'italic',
        textAlign: 'center',
    },

    highlightCell: {
        backgroundColor: '#f0fff0',
    },
    zebraEven: {
        backgroundColor: '#fafafa',
    },
    zebraOdd: {
        backgroundColor: '#ffffff',
    },
});

// Helper function to get the minimum rate for a specific product column
const getMinValueForProduct = (
    rates: RateData[],
    product: string
): number | null => {
    const numericValues = rates
        // Ensure selected_rate is treated as string before parsing
        .map(row => {
            if (row.form !== product || row.selected_rate == null) return NaN;
            // Clean the rate string before parsing
            const cleanedRate = String(row.selected_rate).replace(
                /[^\d.-]/g,
                ''
            );
            return parseFloat(cleanedRate);
        })
        .filter(num => !isNaN(num)); // Filter out any NaNs

    if (numericValues.length === 0) return null;
    return Math.min(...numericValues);
};

// Helper function to format phone number (simple 10-digit US format)
const formatPhoneNumber = (
    phoneNumberString: string | null | undefined
): string => {
    if (!phoneNumberString) return '';
    const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
        return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return phoneNumberString; // Return original if not 10 digits
};

// Parse rate to number (rate in DB is XX.X = percentage)
const getRateNum = (rate: string | number | null | undefined): number => {
    if (rate === null || rate === undefined) return 0;
    const cleanedRate = String(rate).replace(/[^\d.-]/g, '');
    const num = parseFloat(cleanedRate);
    return isNaN(num) ? 0 : num;
};

// Format rate for display: XX.XX%
const formatRateDisplay = (
    rate: string | number | null | undefined
): string => {
    const num = getRateNum(rate);
    return num === 0 ? '-' : num.toFixed(2) + '%';
};

// Format cost per acre: IPA * (rate/100) as $xx.xx
const formatCostPerAcre = (
    rate: string | number | null | undefined,
    ipa: number
): string => {
    const num = getRateNum(rate);
    if (num === 0 || !ipa) return '-';
    const cost = ipa * (num / 100);
    return '$' + cost.toFixed(2);
};

// Helper for min value comparison (numeric)
const formatRateValue = (rate: string | number | null | undefined): string => {
    const num = getRateNum(rate);
    return num === 0 ? '-' : num.toFixed(2);
};

export type PdfLine = {
    label: string;
    ipa: number;
    /** Crop for this line — used to key endorsement rate lookups. */
    crop?: string;
    /** County for this line — used to key endorsement rate lookups. */
    county?: string;
    rates: RateData[];
    selectedCarrier?: string | null;
    selectedProducts?: string[];
};

export type PdfDiscountInfo = {
    fraction: number;
    pct: string;
    due: string;
    excluded_products?: string[];
};

interface RatePdfDocumentProps {
    formData: Partial<RateLookupFormData>;
    rates: RateData[];
    applicableHailProducts: string[];
    /** When provided, one table per line is rendered (includes all location/crop tables) */
    lines?: PdfLine[];
    insuredName?: string;
    accountName?: string;
    ipa?: number;
    identity: UserIdentity;
    cropWestLogoUrl: string;
    watermarkUrl?: string;
    /** Discount info keyed by carrier name. When present, Disc. Rate column is shown. */
    discountInfo?: Record<string, PdfDiscountInfo>;
    /** Selected endorsements: Map of endorsement_type -> endorsement name */
    selectedEndorsements?: Record<string, string>;
    /**
     * Endorsement rates keyed by
     * `${carrier}|${form}|${endorsement}|${crop.toLowerCase()}|${county.toLowerCase()}`.
     * Per-line crop/county is required because rates differ across crops, and
     * a single PDF may render multiple location-crop tables.
     */
    endorsementRateLookup?: Record<string, number>;
}

export const RatePdfDocument: React.FC<RatePdfDocumentProps> = ({
    formData,
    rates,
    applicableHailProducts,
    lines,
    insuredName,
    accountName,
    ipa,
    identity,
    discountInfo,
    selectedEndorsements,
    endorsementRateLookup,
}) => {
    // This helps to render the PDF in the testing environment
    const baseUrl =
        typeof window !== 'undefined'
            ? window.location.origin
            : 'https://hailhelper.app';

    //This should help with the watermark
    const watermarkUrl = `${baseUrl}/img/cwwater64.png`;

    //Company Logo should be gathering it from Supabase Sales Table
    const supabaseBaseUrl =
        'https://mzamzrxfdzwnsuzdpxpt.supabase.co/storage/v1/object/public/attachments/';
    const defaultLogo = `${baseUrl}/img/crop-west-logo.png`;

    // Fix: Check if company_logo is already a full URL or just a path
    const logoUrl = (() => {
        if (!identity?.company_logo || identity.company_logo.trim() === '') {
            return defaultLogo;
        }

        // If it's already a full URL (starts with http), use it directly
        if (identity.company_logo.startsWith('http')) {
            return identity.company_logo;
        }

        // Otherwise, it's a relative path, so prepend the supabase base URL
        return `${supabaseBaseUrl}${identity.company_logo}`;
    })();

    const staticCropWestLogo = `${baseUrl}/img/crop-west-logo.png`;

    // Carrier badge colours (matches QuoteRatesDisplay)
    const pdfCarrierColors: Record<string, { bg: string; color: string }> = {
        RCIS: { bg: '#e6f3f0', color: '#4a9e8c' },
        NAU: { bg: '#e6f6e6', color: '#4a9e4a' },
        'Rain & Hail': { bg: '#e6e6f6', color: '#5b4a9e' },
    };
    const getPdfCarrierColor = (carrier: string) =>
        pdfCarrierColors[carrier] ?? { bg: '#f5f5f5', color: '#666666' };

    const TEAL = '#4a9e8c';
    const GREY_BORDER = '#bdbdbd';
    const HIGHLIGHT_BG = '#e6f3f0';

    // Selection circle rendered as a View circle
    const SelectionCirclePdf = ({ selected }: { selected: boolean }) => (
        <View
            style={{
                width: 14,
                height: 14,
                borderRadius: 7,
                borderWidth: 1.5,
                borderColor: selected ? TEAL : GREY_BORDER,
                backgroundColor: selected ? TEAL : 'transparent',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {selected ? (
                <Text
                    style={{
                        color: '#ffffff',
                        fontSize: 7,
                        fontWeight: 'bold',
                        marginTop: -0.5,
                    }}
                >
                    ✓
                </Text>
            ) : null}
        </View>
    );

    // Carrier badge (chip) rendered as a View
    const CarrierBadge = ({ carrier }: { carrier: string }) => {
        const cc = getPdfCarrierColor(carrier);
        return (
            <View
                style={{
                    backgroundColor: cc.bg,
                    borderRadius: 10,
                    paddingHorizontal: 8,
                    paddingVertical: 2,
                }}
            >
                <Text
                    style={{
                        color: cc.color,
                        fontSize: 8,
                        fontWeight: 'bold',
                    }}
                >
                    {carrier === 'proAg' ? 'Pro Ag' : carrier}
                </Text>
            </View>
        );
    };

    const showDisc =
        discountInfo != null && Object.keys(discountInfo).length > 0;

    // Discount badge rendered as a View (PDF-compatible chip)
    const DiscountBadge = ({ info }: { info: PdfDiscountInfo }) => (
        <View
            style={{
                borderRadius: 8,
                borderWidth: 1,
                borderColor: '#4caf50',
                paddingHorizontal: 4,
                paddingVertical: 1,
            }}
        >
            <Text
                style={{
                    color: '#4caf50',
                    fontSize: 6,
                    fontWeight: 'bold',
                }}
            >
                {info.pct}
                {info.due ? ' | ' + info.due : ''}
            </Text>
        </View>
    );

    // Format discounted rate
    const formatDiscRateDisplay = (
        rate: string | number | null | undefined,
        discFraction: number
    ): string => {
        const num = getRateNum(rate);
        if (num === 0) return '-';
        if (discFraction <= 0) return num.toFixed(2) + '%';
        const discRate = num * (1 - discFraction);
        return discRate.toFixed(2) + '%';
    };

    // Format cost/acre with discount applied
    const formatDiscCostPerAcre = (
        rate: string | number | null | undefined,
        ipaVal: number,
        discFraction: number
    ): string => {
        const num = getRateNum(rate);
        if (num === 0 || !ipaVal) return '-';
        const discRate = num * (1 - discFraction);
        const cost = ipaVal * (discRate / 100);
        return '$' + cost.toFixed(2);
    };

    // Build one table from rates + ipa. Optional title, optional selections.
    const buildOneTable = (
        ratesForTable: RateData[],
        ipaForTable: number,
        title?: string,
        selCarrier?: string | null,
        selProducts?: string[],
        lineCrop?: string,
        lineCounty?: string
    ): React.ReactNode => {
        const lineCropForKey = lineCrop ?? '';
        const lineCountyForKey = lineCounty ?? '';
        const lookupRate = (
            carrier: string,
            product: string,
            endorsement: string
        ): number | null =>
            readSavedEndorsementRate(
                endorsementRateLookup,
                carrier,
                product,
                endorsement,
                lineCropForKey,
                lineCountyForKey
            );
        const rByCarrier: Record<string, Record<string, string | null>> = {};
        const uCarriers = [
            ...new Set(ratesForTable.map(rate => rate.carrier || 'Unknown')),
        ].sort();
        const allProducts = [
            ...new Set(ratesForTable.map(rate => rate.form).filter(Boolean)),
        ].sort() as string[];

        uCarriers.forEach(carrier => {
            rByCarrier[carrier] = {};
            allProducts.forEach(product => {
                const rateInfo = ratesForTable.find(
                    r => r.carrier === carrier && r.form === product
                );
                rByCarrier[carrier][product] =
                    rateInfo?.selected_rate != null
                        ? String(rateInfo.selected_rate)
                        : null;
            });
        });

        const productsToDisplay = applicableHailProducts
            .filter(product => {
                const hasData = uCarriers.some(carrier => {
                    const rate = rByCarrier[carrier]?.[product];
                    const cleanedRate = String(rate).replace(/[^\d.-]/g, '');
                    const num = parseFloat(cleanedRate);
                    return !isNaN(num);
                });
                return hasData && applicableHailProducts.includes(product);
            })
            .sort();

        const carriersWithData = uCarriers.filter(carrier => {
            return productsToDisplay.some(product => {
                const rate = rByCarrier[carrier]?.[product];
                const cleanedRate = String(rate).replace(/[^\d.-]/g, '');
                const num = parseFloat(cleanedRate);
                return !isNaN(num);
            });
        });

        const numberOfProducts = productsToDisplay.length;
        const subColCount = showDisc ? 3 : 2;
        const activeEndorsements = Object.entries(selectedEndorsements || {});
        const endorsementColCount = activeEndorsements.length;
        const totalSubColCount = subColCount + endorsementColCount;
        const productHeaderWidth =
            numberOfProducts > 0 ? `${80 / numberOfProducts}%` : '80%';
        const rateCostColumnWidth =
            numberOfProducts > 0
                ? `${80 / numberOfProducts / totalSubColCount}%`
                : `${80 / totalSubColCount}%`;

        const isHighlighted = (carrier: string, product: string) =>
            selCarrier === carrier &&
            selProducts != null &&
            selProducts.includes(product);

        const isProductSelected = (product: string) =>
            selProducts != null && selProducts.includes(product);

        return (
            <View
                style={{
                    borderWidth: 1,
                    borderColor: '#e0e0e0',
                    borderRadius: 3,
                    marginTop: 10,
                    overflow: 'hidden',
                }}
            >
                {title != null && title !== '' ? (
                    <View style={styles.tableTitleRow}>
                        <Text style={styles.tableTitleCell}>{title}</Text>
                    </View>
                ) : null}

                {/* Header row 1: product names with selection circles */}
                <View
                    style={{
                        flexDirection: 'row',
                        backgroundColor: '#f5f5f5',
                        borderBottomWidth: 1,
                        borderBottomColor: '#e0e0e0',
                        alignItems: 'center',
                        minHeight: 28,
                    }}
                >
                    <View
                        style={{
                            width: '20%',
                            padding: 4,
                            paddingLeft: 8,
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 3,
                        }}
                    >
                        <Text
                            style={{
                                fontWeight: 'bold',
                                fontSize: 9,
                            }}
                        >
                            AIP
                        </Text>
                        {showDisc && (
                            <View
                                style={{
                                    borderRadius: 6,
                                    borderWidth: 0.5,
                                    borderColor: '#999',
                                    paddingHorizontal: 3,
                                    paddingVertical: 1,
                                }}
                            >
                                <Text
                                    style={{
                                        fontSize: 5,
                                        color: '#666',
                                    }}
                                >
                                    Discount | Due
                                </Text>
                            </View>
                        )}
                    </View>
                    {productsToDisplay.map(product => (
                        <View
                            key={product}
                            style={{
                                width: productHeaderWidth,
                                alignItems: 'center',
                                padding: 4,
                                borderLeftWidth: 1,
                                borderLeftColor: '#e0e0e0',
                            }}
                        >
                            <SelectionCirclePdf
                                selected={isProductSelected(product)}
                            />
                            <Text
                                style={{
                                    fontWeight: 'bold',
                                    fontSize: 9,
                                    marginTop: 2,
                                }}
                            >
                                {product}
                            </Text>
                        </View>
                    ))}
                </View>

                {/* Header row 2: Rate / Cost sub-headers */}
                <View
                    style={{
                        flexDirection: 'row',
                        backgroundColor: '#f5f5f5',
                        borderBottomWidth: 1,
                        borderBottomColor: '#e0e0e0',
                        alignItems: 'center',
                        minHeight: 16,
                    }}
                >
                    <View style={{ width: '20%' }}>
                        <Text style={{ fontSize: 1 }}> </Text>
                    </View>
                    {productsToDisplay.flatMap(product => {
                        const cols = [
                            <View
                                key={`${product}-rate`}
                                style={{
                                    width: rateCostColumnWidth,
                                    padding: 2,
                                    borderLeftWidth: 1,
                                    borderLeftColor: '#e0e0e0',
                                }}
                            >
                                <Text
                                    style={{
                                        fontSize: 7,
                                        color: '#888888',
                                        textAlign: 'right',
                                    }}
                                >
                                    Rate
                                </Text>
                            </View>,
                        ];
                        activeEndorsements.forEach(([type, name]) => {
                            cols.push(
                                <View
                                    key={`${product}-endo-${type}`}
                                    style={{
                                        width: rateCostColumnWidth,
                                        padding: 2,
                                    }}
                                >
                                    <Text
                                        style={{
                                            fontSize: 7,
                                            color: '#888888',
                                            textAlign: 'right',
                                        }}
                                    >
                                        {name.length > 15
                                            ? name.replace(
                                                  'Wind Shatter ',
                                                  'WSH '
                                              )
                                            : name}
                                    </Text>
                                </View>
                            );
                        });
                        if (showDisc) {
                            cols.push(
                                <View
                                    key={`${product}-disc`}
                                    style={{
                                        width: rateCostColumnWidth,
                                        padding: 2,
                                    }}
                                >
                                    <Text
                                        style={{
                                            fontSize: 7,
                                            color: '#888888',
                                            textAlign: 'right',
                                        }}
                                    >
                                        Disc. Rate
                                    </Text>
                                </View>
                            );
                        }
                        cols.push(
                            <View
                                key={`${product}-cost`}
                                style={{
                                    width: rateCostColumnWidth,
                                    padding: 2,
                                    borderRightWidth: 1,
                                    borderRightColor: '#e0e0e0',
                                }}
                            >
                                <Text
                                    style={{
                                        fontSize: 7,
                                        color: '#888888',
                                        textAlign: 'right',
                                    }}
                                >
                                    Cost/Acre
                                </Text>
                            </View>
                        );
                        return cols;
                    })}
                </View>

                {/* Data rows */}
                {carriersWithData.map((carrier, idx) => {
                    const isCarrierSel = selCarrier === carrier;
                    const rowBg = idx % 2 === 1 ? '#fafafa' : '#ffffff';
                    const discInfo =
                        discountInfo?.[carrier] ??
                        (carrier === 'proAg' || carrier === 'ProAg'
                            ? discountInfo?.['Pro Ag']
                            : carrier === 'Pro Ag'
                              ? discountInfo?.['proAg']
                              : undefined);
                    const discFrac = discInfo?.fraction ?? 0;
                    return (
                        <View
                            key={carrier}
                            style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                minHeight: 22,
                                borderBottomWidth:
                                    idx < carriersWithData.length - 1 ? 1 : 0,
                                borderBottomColor: '#e0e0e0',
                                backgroundColor: rowBg,
                            }}
                        >
                            {/* Carrier cell: circle + badge + discount badge */}
                            <View
                                style={{
                                    width: '20%',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    paddingLeft: 6,
                                    paddingVertical: 4,
                                    gap: 3,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <SelectionCirclePdf selected={isCarrierSel} />
                                <CarrierBadge carrier={carrier} />
                                {showDisc && discInfo && (
                                    <DiscountBadge info={discInfo} />
                                )}
                            </View>

                            {/* Rate / Disc. Rate / Cost cells */}
                            {productsToDisplay.map(product => {
                                const rateValue = rByCarrier[carrier][product];
                                const highlighted = isHighlighted(
                                    carrier,
                                    product
                                );
                                const cellBg = highlighted
                                    ? HIGHLIGHT_BG
                                    : rowBg;
                                const cellWeight = highlighted
                                    ? 'bold'
                                    : ('normal' as const);
                                // Zero out discount for excluded products
                                const isProductExcluded =
                                    discInfo?.excluded_products?.includes(
                                        product
                                    ) ?? false;
                                const effectiveDiscFrac = isProductExcluded
                                    ? 0
                                    : discFrac;

                                // Compute endorsement total for cost/disc calculations
                                let endorsementTotal = 0;
                                activeEndorsements.forEach(
                                    ([, endorsementName]) => {
                                        const endoRate = lookupRate(
                                            carrier,
                                            product,
                                            endorsementName
                                        );
                                        if (endoRate !== null) {
                                            endorsementTotal += endoRate;
                                        }
                                    }
                                );
                                // Combined rate = base + endorsements (as string for format functions)
                                const combinedRateNum =
                                    getRateNum(rateValue) + endorsementTotal;
                                const combinedRateStr =
                                    endorsementTotal > 0
                                        ? String(combinedRateNum)
                                        : rateValue;

                                return (
                                    <React.Fragment
                                        key={`${carrier}-${product}`}
                                    >
                                        <View
                                            style={{
                                                width: rateCostColumnWidth,
                                                padding: 4,
                                                backgroundColor: cellBg,
                                                borderLeftWidth: 1,
                                                borderLeftColor: '#e0e0e0',
                                            }}
                                        >
                                            <Text
                                                style={{
                                                    fontSize: 9,
                                                    textAlign: 'right',
                                                    fontWeight: cellWeight,
                                                }}
                                            >
                                                {formatRateDisplay(rateValue)}
                                            </Text>
                                        </View>
                                        {activeEndorsements.map(
                                            ([type, endorsementName]) => {
                                                const endoRate = lookupRate(
                                                    carrier,
                                                    product,
                                                    endorsementName
                                                );
                                                return (
                                                    <View
                                                        key={type}
                                                        style={{
                                                            width: rateCostColumnWidth,
                                                            padding: 4,
                                                            backgroundColor:
                                                                cellBg,
                                                        }}
                                                    >
                                                        <Text
                                                            style={{
                                                                fontSize: 9,
                                                                textAlign:
                                                                    'right',
                                                                fontWeight:
                                                                    cellWeight,
                                                            }}
                                                        >
                                                            {endoRate !== null
                                                                ? endoRate.toFixed(
                                                                      2
                                                                  )
                                                                : 'N/A'}
                                                        </Text>
                                                    </View>
                                                );
                                            }
                                        )}
                                        {showDisc && (
                                            <View
                                                style={{
                                                    width: rateCostColumnWidth,
                                                    padding: 4,
                                                    backgroundColor: cellBg,
                                                }}
                                            >
                                                <Text
                                                    style={{
                                                        fontSize: 9,
                                                        textAlign: 'right',
                                                        fontWeight: cellWeight,
                                                        color: '#1b8a6b',
                                                    }}
                                                >
                                                    {formatDiscRateDisplay(
                                                        combinedRateStr,
                                                        effectiveDiscFrac
                                                    )}
                                                </Text>
                                            </View>
                                        )}
                                        <View
                                            style={{
                                                width: rateCostColumnWidth,
                                                padding: 4,
                                                backgroundColor: cellBg,
                                                borderRightWidth: 1,
                                                borderRightColor: '#e0e0e0',
                                            }}
                                        >
                                            <Text
                                                style={{
                                                    fontSize: 9,
                                                    textAlign: 'right',
                                                    fontWeight: cellWeight,
                                                    ...(showDisc
                                                        ? { color: '#1b8a6b' }
                                                        : {}),
                                                }}
                                            >
                                                {showDisc
                                                    ? formatDiscCostPerAcre(
                                                          combinedRateStr,
                                                          ipaForTable,
                                                          effectiveDiscFrac
                                                      )
                                                    : formatCostPerAcre(
                                                          combinedRateStr,
                                                          ipaForTable
                                                      )}
                                            </Text>
                                        </View>
                                    </React.Fragment>
                                );
                            })}
                        </View>
                    );
                })}
            </View>
        );
    };

    return (
        <Document>
            <Page size="A4" style={styles.page} orientation="landscape">
                {/* Main PDF content */}
                <View>
                    <View style={styles.topRow}>
                        <View style={styles.topLeft}>
                            {/* PDF title: Account_name - quote_name */}
                            <Text style={styles.farmName}>
                                {accountName && insuredName
                                    ? `${accountName} - ${insuredName}`
                                    : accountName
                                      ? accountName
                                      : insuredName ||
                                        'Farm/Insured Name Placeholder'}
                            </Text>
                            <Text style={styles.hptToolLabel}>
                                HAIL PRICING TOOL
                            </Text>
                        </View>

                        <View style={styles.rightRow}>
                            <View style={styles.contactRight}>
                                {/* Logo Above Text */}
                                <View style={styles.topLogoBox}>
                                    <Image src={logoUrl} style={styles.logo} />
                                </View>

                                {/* Dynamic Agency/Agent Info */}
                                <Text style={styles.agencyText}>
                                    {identity?.company_name || 'Agency Name'}
                                </Text>
                                <View style={styles.agentRow}>
                                    <Text style={styles.agentText}>
                                        {identity?.fullName || 'Agent Name'}
                                    </Text>
                                    {identity?.agent_number && (
                                        <Text style={styles.agentPhone}>
                                            ☎{' '}
                                            {formatPhoneNumber(
                                                identity.agent_number
                                            )}
                                        </Text>
                                    )}
                                </View>
                                {identity?.email && (
                                    <Text style={styles.agentEmail}>
                                        ✉ {identity.email}
                                    </Text>
                                )}
                            </View>
                        </View>
                    </View>

                    <View style={styles.topSpacer} />

                    {/* Table(s): title in merged row is sufficient; no chips. wrap={false} keeps each table on one page. */}
                    {lines != null && lines.length > 0
                        ? lines.map((line, i) => (
                              <View key={i} wrap={false}>
                                  {buildOneTable(
                                      line.rates,
                                      line.ipa,
                                      line.label,
                                      line.selectedCarrier,
                                      line.selectedProducts,
                                      line.crop,
                                      line.county
                                  )}
                              </View>
                          ))
                        : (() => {
                              const singleTableTitle =
                                  [
                                      formData.county,
                                      formData.crop,
                                      formData.township,
                                      formData.range,
                                  ]
                                      .filter(Boolean)
                                      .join(' – ') +
                                  ` - IPA $${Number(ipa ?? 1).toFixed(2)}`;
                              return (
                                  <View wrap={false}>
                                      {buildOneTable(
                                          rates,
                                          ipa ?? 1,
                                          singleTableTitle,
                                          undefined,
                                          undefined,
                                          formData.crop,
                                          formData.county
                                      )}
                                  </View>
                              );
                          })()}
                </View>
                {/* Watermark: fixed so it appears on every page */}
                {watermarkUrl && (
                    <View
                        fixed
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <Image
                            src={watermarkUrl}
                            style={{
                                width: '75%',
                                height: 'auto',
                                opacity: 0.05,
                                objectFit: 'contain',
                            }}
                        />
                    </View>
                )}
                {/* Fixed footer: disclaimer + logo on every page */}
                <View
                    fixed
                    style={{
                        position: 'absolute',
                        bottom: 12,
                        left: 30,
                        right: 30,
                        alignItems: 'center',
                    }}
                >
                    <Text
                        style={{
                            fontSize: 7,
                            textAlign: 'center',
                            color: '#666666',
                            marginBottom: 4,
                        }}
                    >
                        *Please note these rates are for illustrative purposes
                        only. Actual rates, binding authority etc. will come
                        directly from the AIP. This tool is intended to be used
                        as a guide only.
                    </Text>
                    <View
                        style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 6,
                        }}
                    >
                        <Image
                            src={staticCropWestLogo}
                            style={{
                                width: 60,
                                height: 20,
                                objectFit: 'contain',
                            }}
                        />
                        <Text
                            style={{
                                fontSize: 7,
                                fontStyle: 'italic',
                                color: '#888888',
                            }}
                        >
                            Proud member of the Crop West Insurance Group
                        </Text>
                    </View>
                </View>
            </Page>
        </Document>
    );
};
